/**
 * Клиент backend API.
 *
 * Планшет работает в поле, где связь рвётся, поэтому клиент различает виды
 * ошибок: сетевую (можно и нужно повторить) от отказа сервера (повтор не
 * поможет). Экраны показывают разные подсказки в зависимости от вида.
 */

import type {
  AddressItem,
  AppConfig,
  Application,
  ApplicationListItem,
  ApplicationPatch,
  ApplicationStatus,
  CatalogState,
  Company,
  Consent,
  ConsentKind,
  ConsentText,
  HouseItem,
  LoginResponse,
  OcrResponse,
  SigningSession,
  StreetRequest,
  Subscriber,
  Tariff,
  TvPackage,
  User,
} from './types'

const TOKEN_KEY = 'online-dogovor.token'
const USER_KEY = 'online-dogovor.user'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Повтор того же запроса имеет смысл только для этих кодов. */
  get retryable(): boolean {
    return this.status === 0 || this.status >= 500
  }

  /** Токен истёк или отозван — нужен повторный вход. */
  get unauthorized(): boolean {
    return this.status === 401
  }
}

export class NetworkError extends ApiError {
  constructor(message = 'Нет связи с сервером. Проверьте интернет и повторите.') {
    super(0, message)
    this.name = 'NetworkError'
  }
}

// ── хранение сессии ──────────────────────────────────────────────────────────

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function getStoredUser(): User | null {
  const raw = localStorage.getItem(USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as User
  } catch {
    return null
  }
}

function saveSession(response: LoginResponse): void {
  localStorage.setItem(TOKEN_KEY, response.access_token)
  localStorage.setItem(USER_KEY, JSON.stringify(response.user))
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

// ── низкий уровень ───────────────────────────────────────────────────────────

interface RequestOptions {
  method?: string
  body?: unknown
  formData?: FormData
  auth?: boolean
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, formData, auth = true, signal } = options
  const headers: Record<string, string> = {}

  if (auth) {
    const token = getToken()
    if (!token) throw new ApiError(401, 'Требуется вход в приложение')
    headers.Authorization = `Bearer ${token}`
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let response: Response
  try {
    response = await fetch(path, {
      method,
      headers,
      // FormData уходит как есть: браузер сам выставит boundary.
      body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
      signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new NetworkError()
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const payload = text ? safeJson(text) : null

  if (!response.ok) {
    const detail = (payload as { detail?: unknown } | null)?.detail
    throw new ApiError(response.status, errorMessage(response.status, detail), detail)
  }

  return payload as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return { detail: text }
  }
}

function errorMessage(status: number, detail: unknown): string {
  if (typeof detail === 'string' && detail) return detail
  if (detail && typeof detail === 'object') {
    const message = (detail as { message?: unknown }).message
    if (typeof message === 'string') return message
    // Ошибки валидации Pydantic: [{loc, msg, type}, ...]
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => (item as { msg?: string }).msg)
        .filter((msg): msg is string => Boolean(msg))
      if (messages.length) return messages.join('; ')
    }
  }
  if (status >= 500) return 'Сервер недоступен. Повторите попытку.'
  return `Ошибка запроса (${status})`
}

// ── методы API ───────────────────────────────────────────────────────────────

export const api = {
  async login(login: string, password: string): Promise<LoginResponse> {
    const response = await request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: { login, password },
      auth: false,
    })
    saveSession(response)
    return response
  },

  me(): Promise<User> {
    return request<User>('/api/auth/me')
  },

  config(): Promise<AppConfig> {
    return request<AppConfig>('/api/catalog/config')
  },

  tariffs(): Promise<Tariff[]> {
    return request<Tariff[]>('/api/catalog/tariffs')
  },

  tvPackages(): Promise<TvPackage[]> {
    return request<TvPackage[]>('/api/catalog/tv-packages')
  },

  companies(): Promise<Company[]> {
    return request<Company[]>('/api/catalog/companies')
  },

  /** Перечитать таблицу тарифов немедленно. */
  refreshCatalog(): Promise<CatalogState> {
    return request<CatalogState>('/api/catalog/refresh', { method: 'POST' })
  },

  consentText(): Promise<ConsentText> {
    return request<ConsentText>('/api/catalog/consent-text')
  },

  // ── адресный справочник из биллинга ────────────────────────────────────────

  addressCities(q = '', signal?: AbortSignal): Promise<AddressItem[]> {
    const query = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''
    return request<AddressItem[]>(`/api/address/cities${query}`, { signal })
  },

  addressStreets(cityId: number, q = '', signal?: AbortSignal): Promise<AddressItem[]> {
    const params = new URLSearchParams({ city_id: String(cityId) })
    if (q.trim()) params.set('q', q.trim())
    return request<AddressItem[]>(`/api/address/streets?${params}`, { signal })
  },

  addressHouses(streetId: number, q = '', signal?: AbortSignal): Promise<HouseItem[]> {
    const params = new URLSearchParams({ street_id: String(streetId) })
    if (q.trim()) params.set('q', q.trim())
    return request<HouseItem[]>(`/api/address/houses?${params}`, { signal })
  },

  /** Завести дом, которого нет в справочнике (улицы — только заявкой). */
  createHouse(streetId: number, house: string): Promise<HouseItem> {
    return request<HouseItem>('/api/address/houses', {
      method: 'POST',
      body: { street_id: streetId, house },
    })
  },

  /** Найти абонента по inet-логину. 404 — не найден. */
  /** Записать данные заявки в карточку договора в биллинге (кроме тарифов). */
  pushToBilling(id: string): Promise<Application> {
    return request<Application>(`/api/applications/${id}/billing-card`, { method: 'POST' })
  },

  findSubscriber(login: string): Promise<Subscriber> {
    return request<Subscriber>(`/api/address/subscriber/${encodeURIComponent(login)}`)
  },

  /** Заявка оператору на новую улицу. */
  createStreetRequest(input: {
    city_id: number
    city_title: string
    street_title: string
    comment?: string | null
  }): Promise<StreetRequest> {
    return request<StreetRequest>('/api/address/street-requests', {
      method: 'POST',
      body: input,
    })
  },

  listApplications(
    params: { status?: ApplicationStatus; archived?: boolean; q?: string } = {},
  ): Promise<ApplicationListItem[]> {
    const query = new URLSearchParams()
    if (params.status) query.set('status', params.status)
    if (params.archived !== undefined) query.set('archived', String(params.archived))
    if (params.q?.trim()) query.set('q', params.q.trim())
    const suffix = query.toString() ? `?${query}` : ''
    return request<ApplicationListItem[]>(`/api/applications${suffix}`)
  },

  deleteApplication(id: string): Promise<void> {
    return request<void>(`/api/applications/${id}`, { method: 'DELETE' })
  },

  createApplication(): Promise<Application> {
    return request<Application>('/api/applications', { method: 'POST' })
  },

  getApplication(id: string): Promise<Application> {
    return request<Application>(`/api/applications/${id}`)
  },

  patchApplication(id: string, patch: ApplicationPatch): Promise<Application> {
    return request<Application>(`/api/applications/${id}`, { method: 'PATCH', body: patch })
  },

  /** Распознавание без сохранения — используется для предпросмотра качества. */
  recognizePassport(image: Blob, signal?: AbortSignal): Promise<OcrResponse> {
    const form = new FormData()
    form.append('image', image, 'passport.jpg')
    return request<OcrResponse>('/api/ocr/passport', {
      method: 'POST',
      formData: form,
      signal,
    })
  },

  scanPassport(id: string, image: Blob, overwrite = false): Promise<Application> {
    const form = new FormData()
    form.append('image', image, 'passport.jpg')
    return request<Application>(
      `/api/applications/${id}/passport-scan?overwrite=${overwrite}`,
      { method: 'POST', formData: form },
    )
  },

  addConsent(id: string, kind: ConsentKind): Promise<Consent> {
    return request<Consent>(`/api/applications/${id}/consents`, {
      method: 'POST',
      body: { kind, accepted: true },
    })
  },

  startSigning(id: string, forceNew = false): Promise<SigningSession> {
    return request<SigningSession>(
      `/api/applications/${id}/signing?force_new=${forceNew}`,
      { method: 'POST' },
    )
  },

  getSigning(id: string): Promise<SigningSession> {
    return request<SigningSession>(`/api/applications/${id}/signing`)
  },

  refreshSigning(id: string): Promise<SigningSession> {
    return request<SigningSession>(`/api/applications/${id}/signing/refresh`, {
      method: 'POST',
    })
  },

  /**
   * Скачивает PDF договора.
   *
   * Именно запросом с токеном, а не ссылкой в `<a href>`: эндпоинт защищён
   * Bearer-авторизацией, которую браузер по обычной ссылке не отправит.
   */
  async downloadContract(id: string): Promise<Blob> {
    const token = getToken()
    if (!token) throw new ApiError(401, 'Требуется вход в приложение')

    let response: Response
    try {
      response = await fetch(`/api/applications/${id}/contract.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch {
      throw new NetworkError()
    }
    if (!response.ok) {
      throw new ApiError(response.status, 'Не удалось получить договор')
    }
    return response.blob()
  },
}

/** Открывает PDF в новой вкладке, освобождая object URL после открытия. */
export async function openContract(id: string): Promise<void> {
  const blob = await api.downloadContract(id)
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener')
  // Отзыв сразу после open() ломает просмотр в части браузеров — даём время
  // вкладке подхватить содержимое.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
